import { httpGet } from '../http';
import { onboardingEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import type { ServiceResponse } from '../types';

export interface LenderApiResponse {
  id: number;
  lender: string;
  description?: string;
  parentCompany?: string;
  /**
   * Digital sharing schema identifier (used by sharing + payment-offer APIs).
   * Present in the lender/company API payload even if not always used in UI.
   */
  schemaName?: string;
  /**
   * Creditor portal subdomain (used by sharing flows).
   */
  subDomain?: string;
  showCompanyList?: 'Y' | 'N';
  referralCompanies?: 'Y' | 'N';
  customerPaymentOffer?: 'Y' | 'N';
  addToDebtList?: 'Y' | 'N';
  addToShareListFromProfile?: 'Y' | 'N';
  referenceNumberFormat?: string;
}

export interface LenderParentRecord {
  id: number;
  parentCompany: string;
  description?: string | undefined;
  showCompanyList: boolean;
  referralCompanies: boolean;
  customerPaymentOffer: boolean;
  addToDebtList: boolean;
  addToShareListFromProfile: boolean;
  referenceNumberFormat?: string | undefined;
  childCompany: string[];
  raw: LenderApiResponse;
  // Raw child lender records to allow child-level flag checks (e.g., showCompanyList/addToDebtList)
  childrenRaw?: LenderApiResponse[];
}

export interface AccountTypeApiResponse {
  id: number;
  code: string;
  description: string;
  debtType?: string;
  companyName?: string;
}

export interface AccountTypeRecord {
  id: number;
  code: string;
  label: string;
  debtType?: string | undefined;
  companyName?: string | undefined;
}

const mapParentRecord = (
  parent: LenderApiResponse | null,
  children: string[],
  fallbackName: string,
  fallbackId: number,
  overrides: {
    showCompanyList?: boolean;
    addToDebtList?: boolean;
    addToShareListFromProfile?: boolean;
    referenceNumberFormat?: string | undefined;
  } = {}
): LenderParentRecord => {
  const trimmedName = (parent?.lender ?? fallbackName).trim();
  const derivedShowCompanyList = overrides.showCompanyList ?? (parent?.showCompanyList === 'Y');
  const derivedAddToDebtList = overrides.addToDebtList ?? (parent?.addToDebtList === 'Y');
  const derivedAddToShareListFromProfile = overrides.addToShareListFromProfile ?? (parent?.addToShareListFromProfile === 'Y');
  const derivedReferenceFormat = overrides.referenceNumberFormat ?? parent?.referenceNumberFormat;
  return {
    id: parent?.id ?? fallbackId,
    parentCompany: trimmedName,
    description: parent?.description,
    showCompanyList: derivedShowCompanyList,
    referralCompanies: parent?.referralCompanies === 'Y',
    customerPaymentOffer: parent?.customerPaymentOffer === 'Y',
    addToDebtList: derivedAddToDebtList,
    addToShareListFromProfile: derivedAddToShareListFromProfile,
    referenceNumberFormat: derivedReferenceFormat,
    childCompany: children,
    raw: parent ?? {
      id: fallbackId,
      lender: fallbackName,
      parentCompany: fallbackName,
      showCompanyList: 'N',
      referralCompanies: 'N',
      customerPaymentOffer: 'N',
      addToDebtList: 'N',
      addToShareListFromProfile: 'N',
    },
  };
};

const groupLenders = (data: LenderApiResponse[]): LenderParentRecord[] => {
  const grouped = new Map<string, {
    parent: LenderApiResponse | null;
    childCompany: string[];
    childRecords: LenderApiResponse[];
  }>();

  data.forEach((item) => {
    const lenderName = (item.lender ?? '').trim();
    if (!lenderName) {
      return;
    }

    const parentKey = item.parentCompany?.trim();
    const trimmedParentKey = parentKey && parentKey.length > 0 ? parentKey : undefined;

    if (trimmedParentKey) {
      if (!grouped.has(trimmedParentKey)) {
        grouped.set(trimmedParentKey, { parent: null, childCompany: [], childRecords: [] });
      }
      const entry = grouped.get(trimmedParentKey);
      entry?.childCompany.push(lenderName);
      entry?.childRecords.push(item);
    }

    const effectiveParentLabel = trimmedParentKey ?? lenderName;
    const existing = grouped.get(effectiveParentLabel);
    if (existing) {
      // Only set the parent record when the item truly represents the parent:
      // - When there is no parentKey (top-level company), or
      // - When the lender name matches the effective parent label exactly.
      // Otherwise, do not overwrite the parent with a child record.
      const isTrueParent = !trimmedParentKey || lenderName === effectiveParentLabel;
      if (!existing.parent || isTrueParent) {
        existing.parent = isTrueParent ? item : existing.parent;
      }
      // Keep child records array initialized for top-level entries
      if (!trimmedParentKey) existing.childRecords = existing.childRecords ?? [];
    } else {
      // Initialize the group. Set parent only when this record is a true parent.
      const isTrueParent = !trimmedParentKey || lenderName === effectiveParentLabel;
      grouped.set(effectiveParentLabel, {
        parent: isTrueParent ? item : null,
        childCompany: [],
        childRecords: [],
      });
    }
  });

  return Array.from(grouped.entries()).map(([parentName, value], index) => {
    const parentRecord = value.parent;
    const children = value.childCompany;
    const childRecords = value.childRecords ?? [];
    const fallbackParent: LenderApiResponse = parentRecord ?? {
      id: -(index + 1),
      lender: parentName,
      showCompanyList: 'N',
      referralCompanies: 'N',
      customerPaymentOffer: 'N',
      addToDebtList: 'N',
      addToShareListFromProfile: 'N',
    };
    const idToUse = typeof parentRecord?.id === 'number' ? parentRecord.id : fallbackParent.id;
    const trimmedChildren = Array.from(new Set(children.map((child) => child.trim()).filter(Boolean)));
    const aggregatedShowCompanyList = (parentRecord?.showCompanyList === 'Y') || childRecords.some((child) => child.showCompanyList === 'Y');
    const aggregatedAddToDebtList = (parentRecord?.addToDebtList === 'Y') || childRecords.some((child) => child.addToDebtList === 'Y');
    const aggregatedAddToShareListFromProfile = (parentRecord?.addToShareListFromProfile === 'Y') || childRecords.some((child) => child.addToShareListFromProfile === 'Y');
    const aggregatedReferenceFormat = parentRecord?.referenceNumberFormat || childRecords.find((child) => child.referenceNumberFormat)?.referenceNumberFormat;

    const mapped = mapParentRecord(
      parentRecord ?? fallbackParent,
      trimmedChildren,
      parentName,
      idToUse,
      {
        showCompanyList: aggregatedShowCompanyList,
        addToDebtList: aggregatedAddToDebtList,
        addToShareListFromProfile: aggregatedAddToShareListFromProfile,
        referenceNumberFormat: aggregatedReferenceFormat,
      }
    );
    // Attach child raw records for downstream child-level logic
    return { ...mapped, childrenRaw: childRecords };
  });
};

export class LenderService {
  static async fetchLenders(options: { skipErrorCapture?: boolean } = {}): Promise<ServiceResponse<LenderParentRecord[]>> {
    try {
      const response = await httpGet<LenderApiResponse[]>(
        onboardingEndpoints.listLenders,
        {
          timeout: 10000,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawData = (response?.data as any)?.data ?? response?.data;
      const data = Array.isArray(rawData) ? rawData : [];
      const grouped = groupLenders(
        data.filter((item: any) => Boolean(item?.lender?.trim()))
      );

      return {
        success: true,
        data: grouped,
        message: 'Lenders fetched successfully',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture('lenders_fetch_error', error, {
          category: 'api',
          severity: 'warning',
          context: {
            endpoint: onboardingEndpoints.listLenders,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : 'Failed to fetch lenders',
        timestamp: new Date().toISOString(),
      };
    }
  }

  static async fetchAccountTypesByCompany(
    companyName: string,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<AccountTypeRecord[]>> {
    if (!companyName || !companyName.trim()) {
      return {
        success: true,
        data: [],
        message: 'Company name is required',
        timestamp: new Date().toISOString(),
      };
    }

    try {
      const response = await httpGet<AccountTypeApiResponse[]>(
        onboardingEndpoints.accountTypesByCompany(companyName),
        {
          timeout: 8000,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawData = (response?.data as any)?.data ?? response?.data;
      const data = Array.isArray(rawData) ? rawData : [];
      const mapped: AccountTypeRecord[] = data
        .filter((item: any) => Boolean(item?.code?.trim()))
        .map((item: any) => ({
          id: item.id,
          code: item.code.trim(),
          label: item.description?.trim() || item.code.trim(),
          debtType: item.debtType,
          companyName: item.companyName,
        }));

      return {
        success: true,
        data: mapped,
        message: 'Account types fetched successfully',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture('account_types_fetch_error', error, {
          category: 'api',
          severity: 'warning',
          context: {
            endpoint: onboardingEndpoints.allAccountTypes,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : 'Failed to fetch account types',
        timestamp: new Date().toISOString(),
      };
    }
  }

  static async fetchAllAccountTypes(
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<AccountTypeRecord[]>> {

    try {
      const response = await httpGet<AccountTypeApiResponse[]>(
        onboardingEndpoints.allAccountTypes,
        {
          timeout: 8000,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawData2 = (response?.data as any)?.data ?? response?.data;
      const data = Array.isArray(rawData2) ? rawData2 : [];
      const mapped: AccountTypeRecord[] = data
        .filter((item: any) => Boolean(item?.code?.trim()))
        .map((item: any) => ({
          id: item.id,
          code: item.code.trim(),
          label: item.description?.trim() || item.code.trim(),
          debtType: item.debtType,
        }));

      return {
        success: true,
        data: mapped,
        message: 'Account types fetched successfully',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture('account_types_fetch_error', error, {
          category: 'api',
          severity: 'warning',
          context: {
            endpoint: onboardingEndpoints.allAccountTypes,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : 'Failed to fetch account types',
        timestamp: new Date().toISOString(),
      };
    }
  }
}
